import { useCallback, useEffect, useRef, useState } from "react";
import api from "./api";

/**
 * Feb 2026 · 9 Sep 2026 · Analog zu `useUnreadQuotes` / `useOpenProjects` —
 * roter Badge auf der Übersichts-Kachel und dem Header-Menüpunkt
 * „Rechnungen".
 *
 * Zählt Rechnungen mit `status ∉ {paid, draft, cancelled}` und
 * `type != credit_note` für den Kunden-Scope. Poll 30 s +
 * `visibilitychange`-Refetch + `bumpUnpaidInvoices()` als Sofort-Refresh
 * (nach erfolgreicher „Als bezahlt markieren"-Aktion).
 */
const UNPAID_INVOICES_EVENT = "weissblom:unpaid-invoices-refresh";

export function bumpUnpaidInvoices() {
  try {
    window.dispatchEvent(new CustomEvent(UNPAID_INVOICES_EVENT));
  } catch { /* older browsers — ignore */ }
}

export function useUnpaidInvoices(role) {
  const [total, setTotal] = useState(0);
  const alive = useRef(true);

  const load = useCallback(async () => {
    if (role !== "customer" && role !== "family") return;
    try {
      const { data } = await api.get("/invoices/unpaid-count");
      if (!alive.current) return;
      setTotal(Number(data?.count || 0));
    } catch { /* silent — badge just won't update this cycle */ }
  }, [role]);

  useEffect(() => {
    alive.current = true;
    if (!role) return () => { alive.current = false; };
    load();
    const iv = setInterval(load, 30000);
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    const onBump = () => load();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(UNPAID_INVOICES_EVENT, onBump);
    return () => {
      alive.current = false;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(UNPAID_INVOICES_EVENT, onBump);
    };
  }, [role, load]);

  return { total, refresh: load };
}
